import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import ModalLayout from '@/components/Modal/ModalLayout';
import SignupForm from '@/components/Forms/SignupForm';
import Text from '@/components/Typography/Text';
import { APP_NAME } from '@/constants/config';
import Logo from '@/components/Icons/Logo'; 
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';

const createStyles = (isXS) => StyleSheet.create({
  container: {
    padding: isXS ? 15 : 25,
    paddingTop: isXS ? 0 : 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    width: '100%',
    alignItems: isXS ? 'flex-start' : 'center',
    justifyContent: 'center',
    marginBottom: 32,
    paddingHorizontal: isXS ? 5 : 0,
  },
  logo: {
    width: 48,
    height: 48,
    marginBottom: 24,
  },
  title: {
    marginBottom: 8,
    textAlign: isXS ? 'left' : 'center',
  },
  subtitle: {
    textAlign: isXS ? 'left' : 'center',
  },
});

function SignupModal({ onClose, onAuthSuccess, preserveContext = false }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = token && !isTokenExpired();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const styles = useMemo(() => createStyles(isXS), [isXS]);

  const handleSuccess = useCallback(data => {
    onClose?.();
    onAuthSuccess?.(data);
  }, [onClose, onAuthSuccess]);

  // If user is authenticated and not a ghost, close the modal
  if (isAuthenticated && !isGhost) {
    onClose && onClose();
    return null;
  }
  
  return (
    <ModalLayout title="Create Account" onClose={onClose}>
      <View style={styles.container}>
        <View style={styles.header}>
          {!isXS && (
            <Logo 
              width={styles.logo.width}
              height={styles.logo.height}
              color={colors.text_color_primary}
              style={styles.logo}
            />
          )}
          <Text size={isXS ? 'xl' : 'xxl'} weight='bold' color='primary' style={styles.title}>Create your {APP_NAME} account</Text>
          <Text size='n' color='secondary' style={styles.subtitle}>Enter your email and password to get started.</Text>
        </View>

        <SignupForm onSuccess={handleSuccess} redirectAfterSuccess={!preserveContext} />
      </View>
    </ModalLayout>
  );
} 

SignupModal.modalOptions = {
  width: 480,
  height: 640,
  heightMobile: [640, '92%'],
};

export default SignupModal; 